import { HistoryOutlined } from '@ant-design/icons';
import {
	VaunceAppOTTTicketOnPosType,
	VaunceAppTicketOnPosType,
} from '@kinderlabs-pos/shared-data-type';
import { getUuidV4 } from '@kinderlabs-pos/shared-util';
import { OrderState, StoreInfoState } from '@kinderlabs-pos/state';
import { WithDialog } from '@kinderlabs-pos/ui-atoms';
import { Button, IconButton, Stack, Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import { VaunceAppTicketHistoryDialog } from './VaunceAppTicketHistoryDialog';
import { VaunceAppTicketUserMemberInfoBox } from './common/VaunceAppTicketUserMemberInfoBox';
import { VaunceTicketItemAccordion } from './common/VaunceTicketItemAccordion';
import { getSortedUserIdList } from './common/getSortedUserIdList';

export const VaunceAppTicketOttListContent = ({
	appTicketInfoMap,
}: {
	appTicketInfoMap: {
		[userId: string]: (VaunceAppOTTTicketOnPosType & { canUseTicket: boolean })[];
	};
}) => {
	const userIdList = getSortedUserIdList({ ticketOrCouponInfoMap: appTicketInfoMap });

	return (
		<Stack>
			{userIdList.map((memberId, idx) => {
				const { username, telephone } = appTicketInfoMap[memberId][0];

				return (
					<Stack
						key={idx}
						spacing={1}>
						<VaunceAppTicketUserMemberInfoBox
							memberId={memberId}
							userName={username}
							userTelephone={telephone}
						/>
						{appTicketInfoMap[memberId].map((ott, ottIdx) => {
							return (
								<VaunceTicketItemAccordion
									key={ottIdx}
									ticket={ott}
									canUseTicket={ott.canUseTicket && !ott.isExpired}
									rightPanel={
										<Stack
											width={'33%'}
											direction={'row'}
											justifyContent={'flex-end'}
											spacing={1}>
											<VaunceAppOttUseButton ott={ott} />
											<WithDialog
												dialog={(open, closeDialog) => (
													<VaunceAppTicketHistoryDialog
														ticketId={ott.ticketId}
														ticketType={'ONE_TIME'}
														open={open}
														closeDialog={closeDialog}
													/>
												)}>
												{(openDialog) => (
													<IconButton onClick={openDialog}>
														<HistoryOutlined />
													</IconButton>
												)}
											</WithDialog>
										</Stack>
									}>
									<></>
								</VaunceTicketItemAccordion>
							);
						})}
					</Stack>
				);
			})}
		</Stack>
	);
};

const VaunceAppOttUseButton = ({ ott }: { ott: VaunceAppTicketOnPosType }) => {
	const storeInfo = useAtomValue(StoreInfoState.curStoreInfo);
	const ticketUsages = useAtomValue(OrderState.cart.lines.ticketUsageSelector).filter(
		(usage) => usage.type === 'VAUNCE_APP_OTT'
	);
	const cartDispatch = OrderState.cart.useDispatcher();

	const status =
		ott.ticketStatus !== 'USABLE'
			? '사용불가'
			: ticketUsages.some((u) => u.ticketUsageInfo.ticketUsageId === `${ott.ticketId}`)
			? '사용중'
			: ('사용가능' as const);

	const handleClickButton = () => {
		if (status !== '사용중') {
			cartDispatch({
				type: 'USAGES',
				subAction: {
					type: 'ADD_TICKET',
					cartLineId: getUuidV4(),
					ticketUsage: {
						type: 'ONETIME',
						useId: `${ott.ticketId}`,
						name: ott.ticketTitle,
						ticketId: ott.ticketId,
						ticketInfoId: 0,
						userName: ott.username,
						userTelephone: ott.telephone,
						memberId: ott.memberId,
						ddiziInfo: [
							{
								name: ott.ticketTitle,
								ddiziType: ott.ddiziInfo?.ticketUser === 'PARENTS' ? 'PARENTS' : 'NORMAL',
								minutes: ott.ddiziInfo?.minutes ?? storeInfo.policy.defaultMinutesInCenter,
								hasNoAdditionalCharge: ott.ddiziInfo?.ticketUser === 'PARENTS',
								isNotForStatistics: false,
								available: 'ALL',
							},
						],
					},
				},
			});
		} else {
			cartDispatch({
				type: 'USAGES',
				subAction: { type: 'DELETE', useId: `${ott.ticketId}`, ticketType: 'ONETIME' },
			});
		}
	};

	return (
		<Button
			variant='outlined'
			sx={{ flex: 1 }}
			disabled={ott.ticketStatus !== 'USABLE'}
			color={ott.ticketStatus === 'USABLE' ? 'warning' : 'error'}
			onClick={handleClickButton}>
			<Typography variant='body1'>
				{status === '사용불가'
					? ott.ticketStatus === 'GIFTED'
						? '선물보냄'
						: '사용불가'
					: status === '사용가능'
					? '사용'
					: '사용 취소'}
			</Typography>
		</Button>
	);
};
